import { Tooltip } from '@leland-dev/leland-ui-library';
import Image from 'next/image';
import { type FC, useMemo } from 'react';

import { GoalName } from '../../__generated-gql-types__/globalTypes';

import { type ClientOutcomesCoachFragment } from './__generated-gql-types__/ClientOutcomes.generated';

interface ClientOutcomesHookProps {
  coach: ClientOutcomesCoachFragment;
  maxTags?: number;
  outcomeType?: GoalName;
}
interface ClientOutcomesProps {
  outcomes: Array<{
    name: string;
    logoLink: string;
  }>;
}

const ClientOutcomes: FC<ClientOutcomesProps> = ({ outcomes }) => {
  return (
    <div className="flex gap-1.5">
      {outcomes.map((outcome) => (
        <Tooltip key={outcome.logoLink} content={outcome.name}>
          <Image
            src={outcome.logoLink}
            alt={outcome.name}
            width={28}
            height={28}
            className="size-7 rounded-sm"
          />
        </Tooltip>
      ))}
    </div>
  );
};

export const useClientOutcomes = ({
  coach,
  maxTags = 10,
  outcomeType,
}: ClientOutcomesHookProps) => {
  const allOutcomes =
    coach.outcomes?.reduce((prev, curr) => {
      if (
        curr.__typename === 'CareerOutcomeSummary' &&
        (!outcomeType || outcomeType === GoalName.CAREER)
      ) {
        return [
          ...prev,
          ...curr.companies
            .map((company) => ({
              name: company.name,
              logoLink: company.logoLink ?? '',
            }))
            .filter((outcome) => outcome.logoLink),
        ];
      } else if (
        curr.__typename === 'SchoolOutcomeSummary' &&
        (!outcomeType || outcomeType === GoalName.SCHOOL)
      ) {
        return [
          ...prev,
          ...curr.schools
            .map((school) => ({
              name: school.name,
              logoLink: school.logoLink ?? '',
            }))
            .filter((outcome) => outcome.logoLink),
        ];
      }
      return prev;
    }, []) ?? [];

  const outcomes = allOutcomes.slice(0, maxTags);

  const Content: FC = useMemo(
    () =>
      function ClientOutcome() {
        return <ClientOutcomes outcomes={outcomes} />;
      },
    [outcomes],
  );

  return {
    Content,
    hasOutcomes: outcomes.length > 0,
  };
};
